import type { AtlasSourceDescriptor } from './assetProvider';
import {
  createAtlasFrameId,
  createAtlasUvRect,
  type AtlasAnimationGroup,
  type AtlasFrame,
  type AtlasManifest,
  type AtlasPage,
  type AtlasPixelRect,
} from './atlasTypes';

interface RuntimeAtlasBuilderOptions {
  readonly pageSize?: number;
  readonly padding?: number;
}

interface LoadedAtlasSource {
  readonly descriptor: AtlasSourceDescriptor;
  readonly image: HTMLImageElement;
  readonly width: number;
  readonly height: number;
}

interface MutableAtlasPage {
  readonly id: string;
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  cursorX: number;
  cursorY: number;
  rowHeight: number;
}

const DEFAULT_PAGE_SIZE = 2048;
const DEFAULT_PADDING = 2;

export async function buildRuntimeAtlasManifest(
  sources: readonly AtlasSourceDescriptor[],
  options: RuntimeAtlasBuilderOptions = {},
): Promise<AtlasManifest> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const padding = options.padding ?? DEFAULT_PADDING;
  const loadedSources = await Promise.all(sources.map(loadAtlasSource));

  const mutablePages: MutableAtlasPage[] = [];
  const frames: AtlasFrame[] = [];
  const animationGroups = new Map<string, AtlasAnimationGroup>();

  for (const source of loadedSources) {
    const placement = placeSource(mutablePages, source, pageSize, padding);
    placement.ctx.drawImage(
      source.image,
      placement.rect.x,
      placement.rect.y,
      placement.rect.width,
      placement.rect.height,
    );

    const frameId = createAtlasFrameId(source.descriptor.kind, source.descriptor.id);
    frames.push({
      id: frameId,
      sourceId: source.descriptor.id,
      kind: source.descriptor.kind,
      pageId: placement.id,
      pixelRect: placement.rect,
      uvRect: createAtlasUvRect(placement.rect, pageSize, pageSize),
      drawWidth: source.descriptor.drawWidth,
      drawHeight: source.descriptor.drawHeight,
      pivot: {
        x: source.descriptor.pivotX ?? 0.5,
        y: source.descriptor.pivotY ?? 0.5,
      },
      offset: {
        x: source.descriptor.offsetX ?? 0,
        y: source.descriptor.offsetY ?? 0,
      },
      animated: source.descriptor.animated === true,
      animationGroupId: source.descriptor.animationGroupId,
      sourcePath: source.descriptor.sourcePath,
    });

    if (source.descriptor.animationGroupId) {
      animationGroups.set(source.descriptor.animationGroupId, {
        id: source.descriptor.animationGroupId,
        loop: true,
        frames: [
          {
            frameId,
            durationMs: source.descriptor.animationDurationMs ?? 1000,
          },
        ],
      });
    }
  }

  const animatedSourceCount = loadedSources.filter((source) => source.descriptor.animated === true).length;
  console.info(
    '[World2DAtlas] Runtime atlas source scan complete.',
    {
      sourceCount: loadedSources.length,
      animatedSourceCount,
      pageCount: mutablePages.length,
    },
  );

  const pages: AtlasPage[] = mutablePages.map((page) => ({
    id: page.id,
    width: pageSize,
    height: pageSize,
    image: page.canvas,
  }));

  return {
    source: 'runtime',
    builtAtMs: Date.now(),
    pages,
    frames,
    animationGroups: [...animationGroups.values()],
  };
}

async function loadAtlasSource(descriptor: AtlasSourceDescriptor): Promise<LoadedAtlasSource> {
  const image = await loadImage(descriptor.sourcePath);
  const width = normalizePixelSize(descriptor.sourceWidth, image.naturalWidth);
  const height = normalizePixelSize(descriptor.sourceHeight, image.naturalHeight);
  if (width <= 0 || height <= 0) {
    throw new Error(`Atlas source '${descriptor.id}' resolved to an empty image.`);
  }
  return {
    descriptor,
    image,
    width,
    height,
  };
}

function normalizePixelSize(preferred: number | undefined, fallback: number): number {
  if (typeof preferred === 'number' && Number.isFinite(preferred) && preferred > 0) {
    return Math.round(preferred);
  }
  return Math.max(1, Math.round(fallback));
}

function placeSource(
  pages: MutableAtlasPage[],
  source: LoadedAtlasSource,
  pageSize: number,
  padding: number,
): MutableAtlasPage & { rect: AtlasPixelRect } {
  const requiredWidth = source.width + padding;
  const requiredHeight = source.height + padding;
  if (requiredWidth > pageSize || requiredHeight > pageSize) {
    throw new Error(`Atlas source '${source.descriptor.id}' is larger than the atlas page size.`);
  }

  let page = pages[pages.length - 1];
  if (!page) {
    page = createMutableAtlasPage(pages.length, pageSize);
    pages.push(page);
  }

  if (page.cursorX + requiredWidth > pageSize) {
    page.cursorX = 0;
    page.cursorY += page.rowHeight;
    page.rowHeight = 0;
  }

  if (page.cursorY + requiredHeight > pageSize) {
    page = createMutableAtlasPage(pages.length, pageSize);
    pages.push(page);
  }

  const rect: AtlasPixelRect = {
    x: page.cursorX,
    y: page.cursorY,
    width: source.width,
    height: source.height,
  };

  page.cursorX += requiredWidth;
  page.rowHeight = Math.max(page.rowHeight, requiredHeight);

  return {
    ...page,
    rect,
  };
}

function createMutableAtlasPage(pageIndex: number, pageSize: number): MutableAtlasPage {
  const canvas = document.createElement('canvas');
  canvas.width = pageSize;
  canvas.height = pageSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to initialize runtime atlas canvas context.');
  }
  return {
    id: `runtime-page-${pageIndex}`,
    canvas,
    ctx,
    cursorX: 0,
    cursorY: 0,
    rowHeight: 0,
  };
}

function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load atlas source '${path}'.`));
    image.src = path;
  });
}
